import React, { createContext, useReducer, useEffect, useContext, ReactNode, useCallback } from 'react';
import { setOnUnauthorized } from '../services/apiClient';

export interface AuthUser {
  id: number;
  username: string;
  isAdmin: boolean;
  permissions: string[];
  subModules: Record<string, unknown> | null;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
}

type Action =
  | { type: 'LOGIN'; token: string; user: AuthUser }
  | { type: 'LOGOUT' };

const initialState: AuthState = {
  token: null,
  user: null,
  isAuthenticated: false,
};

const reducer = (state: AuthState, action: Action): AuthState => {
  switch (action.type) {
    case 'LOGIN':
      return { token: action.token, user: action.user, isAuthenticated: true };
    case 'LOGOUT':
      return initialState;
    default:
      return state;
  }
};

const STORAGE_KEY = 'scrapper_admin_auth';

const loadInitial = (): AuthState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState;
    const parsed = JSON.parse(raw) as { token: string; user: AuthUser };
    if (!parsed?.token || !parsed?.user) return initialState;
    return { token: parsed.token, user: parsed.user, isAuthenticated: true };
  } catch {
    return initialState;
  }
};

interface AuthContextType extends AuthState {
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, undefined, loadInitial);

  useEffect(() => {
    if (state.token && state.user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ token: state.token, user: state.user }));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [state.token, state.user]);

  const login = (token: string, user: AuthUser) => dispatch({ type: 'LOGIN', token, user });
  const logout = useCallback(() => dispatch({ type: 'LOGOUT' }), []);

  useEffect(() => {
    setOnUnauthorized(() => logout());
    return () => setOnUnauthorized(null);
  }, [logout]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
